/**
 * Recover visual baseline comparisons from a Playwright test result.
 *
 * Playwright's snapshot matchers do not report what they compared; they only
 * leave attachments behind. `toHaveScreenshot()` / `toMatchSnapshot()` attach
 * `<stem>-expected`, `<stem>-actual`, `<stem>-diff` and, on a retried failure,
 * `<stem>-previous`. Those four share a stem, and the stem is the snapshot's
 * identity within the test.
 *
 * This module is pure — it groups names and reads a message, and touches no
 * filesystem. Copying the images out is `artifact-manager.copyVisualArtifacts`.
 */

import type { VisualSource, VisualStatus } from '@testrelic/core';
import { parseDiffMessage, isSizeMismatchFailure } from './visual-metrics.js';
import { VISUAL_DOM_ATTACHMENT, type VisualDomRecord } from './visual-expect.js';

interface Attachment {
  name: string;
  contentType: string;
  path?: string;
  body?: Buffer;
}

/**
 * A comparison found in the attachments, still pointing at Playwright's temp
 * files. `artifact-manager` turns this into the report-facing
 * `VisualComparison` once the images have been copied somewhere durable.
 */
export interface VisualCandidate {
  readonly name: string;
  readonly status: VisualStatus;
  readonly source: VisualSource;
  readonly expectedPath?: string;
  readonly actualPath?: string;
  readonly diffPath?: string;
  readonly previousPath?: string;
  readonly diffPixels?: number;
  readonly actualWidth?: number;
  readonly actualHeight?: number;
  /** The DOM comparison for this snapshot, when one was made. */
  readonly dom?: VisualDomRecord;
}

/** Attachment name carrying the explicit matcher's JSON side-channel. */
export const VISUAL_META_ATTACHMENT = 'testrelic-visual';

/** The four roles a snapshot image can play, as Playwright suffixes them. */
type VisualRole = 'expected' | 'actual' | 'diff' | 'previous';

const VISUAL_SUFFIX_RE = /^(.+)-(expected|actual|diff|previous)(\.[A-Za-z0-9]+)$/;

/** One stem's worth of images, before a status is decided. */
interface StemGroup {
  expected?: string;
  actual?: string;
  diff?: string;
  previous?: string;
}

/**
 * Metadata the `toMatchVisualBaseline` matcher attaches alongside its images.
 *
 * It exists because a passing comparison is otherwise invisible: Playwright
 * attaches nothing at all when the images match, so without this the report
 * could only ever show the failures.
 */
export interface VisualMetaRecord {
  readonly stem: string;
  readonly name: string;
  readonly status: VisualStatus;
}

/** Read and validate the explicit matcher's metadata attachment. */
export function parseVisualMeta(attachments: readonly Attachment[]): Map<string, VisualMetaRecord> {
  const byStem = new Map<string, VisualMetaRecord>();

  for (const attachment of attachments) {
    if (attachment.name !== VISUAL_META_ATTACHMENT || !attachment.body) continue;
    try {
      const parsed: unknown = JSON.parse(attachment.body.toString('utf-8'));
      if (!Array.isArray(parsed)) continue;
      for (const entry of parsed) {
        const record = toMetaRecord(entry);
        if (record) byStem.set(record.stem, record);
      }
    } catch {
      // A malformed side-channel costs us the pass-case detail, nothing more.
    }
  }

  return byStem;
}

const VALID_STATUSES: readonly string[] = ['passed', 'failed', 'new', 'updated'];

// SAFETY: parsed from an attachment body written by the worker process; every
// field is checked before use.
function toMetaRecord(entry: unknown): VisualMetaRecord | null {
  if (typeof entry !== 'object' || entry === null) return null;
  const e = entry as Record<string, unknown>;
  if (typeof e.stem !== 'string' || !e.stem) return null;
  if (typeof e.name !== 'string' || !e.name) return null;
  if (typeof e.status !== 'string' || !VALID_STATUSES.includes(e.status)) return null;
  return { stem: e.stem, name: e.name, status: e.status as VisualStatus };
}

/**
 * Read the DOM comparison side-channel, keyed by the same stem the images use.
 *
 * Written by `visual-expect`; absent whenever the assertion used Playwright's
 * own `expect`, or the page had no DOM baseline yet.
 */
export function parseVisualDom(
  attachments: readonly Attachment[],
): Map<string, VisualDomRecord> {
  const byStem = new Map<string, VisualDomRecord>();

  for (const attachment of attachments) {
    if (attachment.name !== VISUAL_DOM_ATTACHMENT || !attachment.body) continue;
    try {
      const parsed: unknown = JSON.parse(attachment.body.toString('utf-8'));
      if (!Array.isArray(parsed)) continue;
      for (const entry of parsed) {
        // SAFETY: written by the worker process; the two fields read before
        // anything else uses it are checked here.
        if (typeof entry !== 'object' || entry === null) continue;
        const record = entry as Record<string, unknown>;
        if (typeof record.stem !== 'string' || !Array.isArray(record.changes)) continue;
        byStem.set(record.stem, entry as VisualDomRecord);
      }
    } catch {
      // A malformed side-channel costs the element list, nothing more.
    }
  }

  return byStem;
}

/** Group every `<stem>-<role>.<ext>` attachment that has a file behind it. */
function groupBySnapshotStem(attachments: readonly Attachment[]): Map<string, StemGroup> {
  const groups = new Map<string, StemGroup>();

  for (const attachment of attachments) {
    if (!attachment.path) continue;
    const match = VISUAL_SUFFIX_RE.exec(attachment.name);
    if (!match) continue;

    const [, stem, role] = match;
    const group = groups.get(stem as string) ?? {};
    group[role as VisualRole] = attachment.path;
    groups.set(stem as string, group);
  }

  return groups;
}

/**
 * Decide whether a stem is really a snapshot comparison.
 *
 * An ordinary user attachment named `report-actual.json` would otherwise be
 * mistaken for one. A genuine comparison always produces an `actual` next to
 * either the baseline it was compared against or the diff it produced, so
 * requiring that pair costs nothing real and rejects the lookalikes. Stems the
 * explicit matcher has vouched for skip this check — it attaches a lone
 * baseline on a pass, which is the one legitimate single-image case.
 */
function isCredibleComparison(group: StemGroup): boolean {
  return Boolean(group.actual && (group.expected || group.diff));
}

/** Derive a status from the images alone, for natively-produced comparisons. */
function deriveStatus(
  group: StemGroup,
  updatingSnapshots: boolean,
  failureMessage: string | null | undefined,
): VisualStatus {
  if (group.diff) return 'failed';
  // A size mismatch also produces no diff — the comparator cannot draw one
  // when the two images have different dimensions, so it attaches expected +
  // actual and throws. Absence of a diff therefore does NOT imply "no baseline
  // existed": read the message before concluding that. Without this a genuine
  // failure was reported as a freshly written baseline, it was missing from
  // visualFailures / visualFailedCount / visualDiffPath, and because
  // applyMetrics only targets failed stems the parsed dimensions were dropped
  // too (making SIZE_MISMATCH_RE dead in practice).
  if (isSizeMismatchFailure(failureMessage)) return 'failed';
  // Playwright attached a baseline and an actual but no diff: it had no
  // baseline and wrote one. That is not a pass — nothing was compared.
  return updatingSnapshots ? 'updated' : 'new';
}

/**
 * Attach the pixel count to the comparison it actually describes.
 *
 * The failure message belongs to whichever assertion threw. When it echoes a
 * snapshot name, that name is matched against the stems; otherwise the numbers
 * are only trustworthy if exactly one comparison failed, and are dropped when
 * several did rather than being pinned on an arbitrary one.
 */
function selectMetricsTarget(
  failed: readonly string[],
  snapshotName: string | undefined,
): string | null {
  if (failed.length === 0) return null;
  if (failed.length === 1) return failed[0] as string;
  if (!snapshotName) return null;

  const base = snapshotName.replace(/\.[A-Za-z0-9]+$/, '');
  const matches = failed.filter((stem) => stem === base || stem.endsWith(`/${base}`));
  return matches.length === 1 ? (matches[0] as string) : null;
}

export interface ParseVisualOptions {
  /** True when the run was invoked with `--update-snapshots`. */
  readonly updatingSnapshots?: boolean;
  /** Concatenated failure messages for the test attempt. */
  readonly failureMessage?: string | null;
}

/**
 * Extract every visual comparison recorded against one test attempt.
 *
 * Returns an empty array — never throws — for a test that made no visual
 * assertions, which is the overwhelming majority of them.
 */
export function parseVisualAttachments(
  attachments: readonly Attachment[],
  options: ParseVisualOptions = {},
): VisualCandidate[] {
  if (attachments.length === 0) return [];

  const meta = parseVisualMeta(attachments);
  const dom = parseVisualDom(attachments);
  const groups = groupBySnapshotStem(attachments);
  if (groups.size === 0) return [];

  const updating = options.updatingSnapshots === true;
  const candidates: VisualCandidate[] = [];

  for (const [stem, group] of groups) {
    const vouched = meta.get(stem);
    if (!vouched && !isCredibleComparison(group)) continue;

    candidates.push({
      name: vouched?.name ?? stem,
      status: vouched?.status ?? deriveStatus(group, updating, options.failureMessage),
      source: vouched ? 'toMatchVisualBaseline' : 'toHaveScreenshot',
      ...(group.expected ? { expectedPath: group.expected } : {}),
      ...(group.actual ? { actualPath: group.actual } : {}),
      ...(group.diff ? { diffPath: group.diff } : {}),
      ...(group.previous ? { previousPath: group.previous } : {}),
      ...(dom.has(stem) ? { dom: dom.get(stem) as VisualDomRecord } : {}),
    });
  }

  return applyMetrics(candidates, groups, options.failureMessage);
}

/** Fold the parsed failure numbers into the one comparison they belong to. */
function applyMetrics(
  candidates: readonly VisualCandidate[],
  groups: ReadonlyMap<string, StemGroup>,
  failureMessage: string | null | undefined,
): VisualCandidate[] {
  const parsed = parseDiffMessage(failureMessage);
  if (parsed.diffPixels === undefined && parsed.actualWidth === undefined) {
    return [...candidates];
  }

  const failedStems = candidates.filter((c) => c.status === 'failed').map((c) => stemOf(c, groups));
  const target = selectMetricsTarget(failedStems, parsed.snapshotName);
  if (target === null) return [...candidates];

  return candidates.map((candidate) =>
    stemOf(candidate, groups) === target
      ? {
          ...candidate,
          ...(parsed.diffPixels !== undefined ? { diffPixels: parsed.diffPixels } : {}),
          ...(parsed.actualWidth !== undefined ? { actualWidth: parsed.actualWidth } : {}),
          ...(parsed.actualHeight !== undefined ? { actualHeight: parsed.actualHeight } : {}),
        }
      : candidate,
  );
}

/**
 * Recover the stem a candidate came from.
 *
 * The explicit matcher renames a candidate to the author's chosen name, so the
 * name cannot be used to look it back up among the groups.
 */
function stemOf(candidate: VisualCandidate, groups: ReadonlyMap<string, StemGroup>): string {
  if (groups.has(candidate.name)) return candidate.name;
  for (const [stem, group] of groups) {
    if (
      group.actual === candidate.actualPath &&
      group.expected === candidate.expectedPath &&
      group.diff === candidate.diffPath
    ) {
      return stem;
    }
  }
  return candidate.name;
}
